<template>
  <div
    class="tei-select-object-hotspot"
    :class="{
      'tei-select-object-hotspot--enabled': !disabled,
      'tei-select-object-hotspot--selected': selected,
      [`tei-select-object-hotspot--${state}`]: state,
    }"
    :style="hotspotStyle"
  >
    <div
      :id="selectId"
      class="tei-select-object-hotspot__content"
      tabindex="0"
      :aria-disabled="disabled ? 'true' : 'false'"
      :aria-label="ariaLabel"
      aria-live="polite"
      @click="selectedChanged"
      @keyup.enter="selectedChanged"
    >
      <div class="tei-select-object-hotspot__dash-ring" />
      <TEIOptionStatusIcon
        v-if="selected || state"
        class="tei-select-object-hotspot__status-icon"
        :status="state || 'selected'"
        :style="statusIconStyle"
      />
    </div>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { extractTextFromHtml } from '@/lib/utils';
import { getBoundingBox } from '@/lib/utils/grid';
import TEIOptionStatusIcon from '@/components/teis/TEIOptionStatusIcon.vue';

const nebulaSpace7x = parseInt(nebulaTokens.space['7x'], 10);
const nebulaSpace4x = parseInt(nebulaTokens.space['4x'], 10);
const STATUS_ICON_MIN_PADDING = 20;

export default {
  name: 'TEISelectObjectHotspot',
  components: {
    TEIOptionStatusIcon,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    points: {
      type: Array,
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    accessibilityContent: {
      type: String,
      default: null,
    },
    state: {
      type: String,
      default: null, // correct, incorrect
    },
    isDirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selected-changed'],
  data() {
    return {
      selectId: uuid(),
    };
  },
  computed: {
    ariaLabel() {
      const desc = extractTextFromHtml(this.accessibilityContent);
      if (!this.disabled) {
        if (this.isDirty) {
          return this.selected
            ? this.$t('You have selected %(desc)s. Press enter to deselect.', { desc })
            : this.$t('You have deselected %(desc)s. Press enter to select.', { desc });
        }
        return this.selected
          ? this.$t('%(desc)s is selected. Press enter to deselect.', { desc })
          : this.$t('%(desc)s is not selected. Press enter to select.', { desc });
      }
      if (this.state) {
        return this.state === 'correct'
          ? this.$t('%(desc)s is correct', { desc })
          : this.$t('%(desc)s is incorrect', { desc });
      }
      return this.selected
        ? this.$t('%(desc)s is selected.', { desc })
        : this.$t('%(desc)s is not selected', { desc });
    },
    boundingBox() {
      return getBoundingBox(this.points);
    },
    hotspotStyle() {
      return {
        ...Object.keys(this.boundingBox).reduce((style, prop) => ({
          ...style,
          [prop]: `${this.boundingBox[prop]}px`,
        }), {}),
      };
    },
    statusIconStyle() {
      const { width, height } = this.boundingBox;
      const boxSize = Math.min(width, height);
      let iconSize = boxSize - (STATUS_ICON_MIN_PADDING * 2);
      if (iconSize > nebulaSpace7x) {
        iconSize = nebulaSpace7x;
      } else if (iconSize < nebulaSpace4x) {
        iconSize = nebulaSpace4x;
      }
      return {
        width: `${iconSize}px`,
        height: `${iconSize}px`,
      };
    },
  },
  methods: {
    selectedChanged() {
      if (this.disabled) return;
      this.$emit('selected-changed', { id: this.id });
    },
  },
};
</script>

<style lang="stylus">
.tei-select-object-hotspot {
  position: absolute;

  &__content {
    background-color: var(--tei-select-object-hotspot-color);
    border-radius: $nebula-space-3x;
    display: block;
    height: 100%;
    position: relative;
    transition: $nebula-transition-default;
    width: 100%;
  }

  &__dash-ring {
    border: 5px dashed $nebula-color-white;
    border-radius: $nebula-space-2x + $nebula-space-half;
    inset: $nebula-space-1x;
    pointer-events: none;
    position: absolute;
  }

  &__status-icon {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &--enabled {
    .tei-select-object-hotspot__content {
      cursor: pointer;

      &:hover {
        background-color: var(--tei-select-object-hotspot-hover-color);
      }
    }
  }

  &--selected {
    .tei-select-object-hotspot__content {
      background-color: var(--tei-select-object-hotspot-active-color);
    }
  }

  &--correct {
    .tei-select-object-hotspot__content {
      background-color: var(--tei-select-object-hotspot-correct-color);
    }
  }

  &--incorrect {
    .tei-select-object-hotspot__content {
      background-color: var(--tei-select-object-hotspot-incorrect-color);
    }
  }
}
</style>
